import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { X, Plus, Search, Check, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ImpactCategorySelector - A reusable component for selecting impact categories
 * 
 * Features:
 * - Predefined categories with emojis
 * - Custom category input (free typing)
 * - Multi-select support
 * - Autocomplete/search
 * - Collapsible view
 */
const ImpactCategorySelector = ({ 
  value = [], 
  onChange, 
  maxSelections = 5,
  mode = 'multi', // 'single' or 'multi'
  showDescription = true,
  collapsible = true,
  placeholder = "Search or type a category..."
}) => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(!collapsible);
  const [isLoading, setIsLoading] = useState(true);
  const inputRef = useRef(null);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/impact-categories`);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching impact categories:', error);
        // Fallback categories
        setCategories([
          { id: 'ai_tech', name: 'AI & Technology', emoji: '🤖' },
          { id: 'healthcare', name: 'Healthcare & Wellbeing', emoji: '🏥' },
          { id: 'education', name: 'Education & Learning', emoji: '📚' },
          { id: 'climate', name: 'Climate & Sustainability', emoji: '🌍' },
          { id: 'civic', name: 'Civic Innovation & Governance', emoji: '🏛️' },
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Filter categories based on search
  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if a category is selected
  const isSelected = (categoryName) => {
    if (mode === 'single') {
      return value === categoryName;
    }
    return value.includes(categoryName);
  };

  // Handle category selection
  const handleSelect = (categoryName) => {
    if (mode === 'single') {
      onChange(categoryName);
      return;
    }

    if (isSelected(categoryName)) {
      // Deselect
      onChange(value.filter(v => v !== categoryName));
    } else {
      // Select (check max)
      if (value.length < maxSelections) {
        onChange([...value, categoryName]);
      }
    }
  };

  // Handle custom category addition
  const handleAddCustom = () => {
    const customCategory = searchTerm.trim();
    if (customCategory && !isSelected(customCategory) && value.length < maxSelections) {
      if (mode === 'single') {
        onChange(customCategory);
      } else {
        onChange([...value, customCategory]);
      }
      setSearchTerm('');
    }
  };

  // Remove a selected category
  const handleRemove = (categoryName) => {
    if (mode === 'single') {
      onChange('');
    } else {
      onChange(value.filter(v => v !== categoryName));
    }
  };

  // Check if custom category can be added
  const canAddCustom = searchTerm.trim() && 
    !categories.some(c => c.name.toLowerCase() === searchTerm.toLowerCase()) &&
    !isSelected(searchTerm.trim()) &&
    (mode === 'single' || value.length < maxSelections);

  return (
    <div className="space-y-3">
      {/* Description */}
      {showDescription && (
        <p className="text-sm text-gray-600">
          Select the impact area your idea addresses. This helps showcase platform impact 
          <span className="text-purple-600 font-medium"> without us accessing your actual ideas</span>.
        </p>
      )}

      {/* Selected categories (chips) */}
      {((mode === 'multi' && value.length > 0) || (mode === 'single' && value)) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {mode === 'single' ? (
            value && (
              <Badge 
                key={value}
                variant="secondary"
                className="bg-purple-100 text-purple-800 py-1 px-3 text-sm flex items-center gap-1"
              >
                {categories.find(c => c.name === value)?.emoji || '💡'} {value}
                <button 
                  onClick={() => handleRemove(value)}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )
          ) : (
            value.map(cat => (
              <Badge 
                key={cat}
                variant="secondary"
                className="bg-purple-100 text-purple-800 py-1 px-3 text-sm flex items-center gap-1"
              >
                {categories.find(c => c.name === cat)?.emoji || '💡'} {cat}
                <button 
                  onClick={() => handleRemove(cat)}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
      )}

      {/* Expand/Collapse toggle */}
      {collapsible && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-gray-600"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide categories
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              {mode === 'single' && !value ? 'Select impact area' : 'Change selection'}
            </>
          )}
        </Button>
      )}

      {/* Category selector (expandable) */}
      {isExpanded && (
        <div className="border rounded-lg p-4 bg-gray-50/50">
          {/* Search input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (canAddCustom) {
                    handleAddCustom();
                  } else if (filteredCategories.length === 1) {
                    handleSelect(filteredCategories[0].name);
                    setSearchTerm('');
                  }
                }
              }}
              className="pl-10 bg-white"
            />
          </div>

          {/* Custom category button */}
          {canAddCustom && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCustom}
              className="mb-4 flex items-center gap-2 text-purple-600 border-purple-300"
            >
              <Plus className="w-4 h-4" />
              Add &ldquo;{searchTerm.trim()}&rdquo; as custom category
            </Button>
          )}

          {/* Category grid */}
          {isLoading ? (
            <div className="text-center py-4 text-gray-500">Loading categories...</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {filteredCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleSelect(category.name)}
                  disabled={!isSelected(category.name) && mode === 'multi' && value.length >= maxSelections}
                  className={`
                    flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-all
                    ${isSelected(category.name) 
                      ? 'bg-purple-100 border-2 border-purple-500 text-purple-800' 
                      : 'bg-white border border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                    }
                    ${!isSelected(category.name) && mode === 'multi' && value.length >= maxSelections 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'cursor-pointer'
                    }
                  `}
                >
                  <span className="text-lg">{category.emoji}</span>
                  <span className="truncate flex-1">{category.name}</span>
                  {isSelected(category.name) && (
                    <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Max selection hint */}
          {mode === 'multi' && (
            <p className="text-xs text-gray-500 mt-3">
              {value.length}/{maxSelections} categories selected
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ImpactCategorySelector;
